<script setup lang="ts">
import { ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { STATUS_OPEN, STATUS_CLOSED, LANG_ENGLISH, STATUS, INSTA } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useHotelManagementStore } from '@/store/HotelManagementStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useNotesManagementStore } from '@/store/NotesManagementStore';
import { usePreferredManagementStore } from '@/store/PreferredManagementStore';
import { useViewComposable } from '@/views/composable';
import { formatDate } from '@/utils/dateUtils';
import { FilterMatchMode, FilterOperator, FilterService } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import Divider from 'primevue/divider';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ListNote from '@/components/modal/ListNote.vue';
import UpdatePreferredModal from '@/components/modal/UpdatePreferredModal.vue';
import AddHotelModal from '@/components/modal/AddHotelModal.vue';
import ConfirmDeleteModal from '@/components/modal/ConfirmDeleteModal.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import ImportFileModal from '@/components/modal/ImportFileModal.vue';
import ViewHotelDataModal from '@/components/modal/ViewHotelDataModal.vue';
import ViewVersionRevertModal from '@/components/modal/ViewVersionRevertModal.vue';
import ViewRoomExclusions from '@/components/modal/ViewRoomExclusions.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';

const toast = useToast();
const { t } = useI18n({ useScope: 'global' });
const hotelManagementStore = useHotelManagementStore();
const notesManagementStore = useNotesManagementStore();
const packageManagementStore = usePackageManagementStore();
const preferredManagementStore = usePreferredManagementStore();
const groupTypeManagement = useGroupTypeStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations = userAuthorizationsStore.permissionByRole;
const { apiOrder, styleColumnExport, renderNameFileExport, formatRoomExport, convertTimeZero } = useViewComposable();

const filtersPackage = ref();
const filtersHotel = ref();
const dialog = useDialog();

const hotels = ref([]);
const groupTypeList = ref([]);
const avgPaxRoomData = ref(2.2);
const avgPaxRoomTempoary = ref(0);
const isEditAvgPaxRoom = ref(false);
const paxRoomNumbers = ref({});
const editingPaxRoomNumbers = ref([]);
const sheetsDataPackage = ref<any>([]);
const sheetsDataHotel = ref<any>([]);
const destinationList = ref<any>([]);
const currentLanguage = getCurrentLocale();
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const isLoading = ref(false);
const isLoadingImport = ref(false);
const isShowModal = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);
const isPackageTab = ref(false);

const widthList = [20, 12, 50, 50, 20, 20, 10, 20, 20, 10, 10, 20, 40, 20, 10, 20, 20, 20, 10, 10, 10];
const sheetColumnStyle = styleColumnExport(widthList);

FilterService.register('filterDates', (dates, filterValue) => {
  const datesFormat = handleDisplayDate(dates, ' ');
  return !filterValue || datesFormat.includes(filterValue);
});

const initFiltersPackage = () => {
  filtersPackage.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    code: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    chainName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destSVId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    groupTypes: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    roomCategoryCallOrder: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'hotelPreferred.sts': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    bookingDate: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    travelDate: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    blackoutDate: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    insta: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    hotelType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: STATUS_OPEN, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFiltersPackage();

const initFiltersHotel = () => {
  filtersHotel.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    code: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    chainName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destSVId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    groupTypesHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    roomCategoryCallOrderHotel: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'hotelPreferredHotel.sts': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    bookingDateHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    travelDateHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    blackoutDateHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    instaHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    hotelTypeHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    statusHotel: { operator: FilterOperator.OR, constraints: [{ value: STATUS_OPEN, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFiltersHotel();

const editAvgPaxRoom = () => {
  avgPaxRoomTempoary.value = avgPaxRoomData.value;
  isEditAvgPaxRoom.value = true;
};

const saveAvgPaxRoom = () => {
  if (!avgPaxRoomTempoary.value) {
    toast.add({ severity: 'error', summary: '', detail: t('validate.required_field', { field: t('management.hotel.avg_pax_room') }) });
    return;
  }
  hotelManagementStore
    .updateAvgPaxRoom({ maxRoom: 0, groupTypes: [], avgPaxRoom: avgPaxRoomTempoary.value })
    .then(() => {
      avgPaxRoomData.value = avgPaxRoomTempoary.value;
      isEditAvgPaxRoom.value = false;
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const cancelEditAvgPaxRoom = () => {
  isEditAvgPaxRoom.value = false;
};

const formatDataExportPackage = (listData: any, isExportVersion?: boolean) => {
  return listData?.map((item: any) => {
    const dataPackage: any = {};
    dataPackage[t('management.hotel.tts_id')] = item.hCode;
    dataPackage[t('management.hotel.sv_id')] = item.code;
    dataPackage[`${t('management.hotel.hotel_name')} (EN)`] = item.name;
    dataPackage[`${t('management.hotel.hotel_name')} (FR)`] = item.nameFr;
    dataPackage[`${t('management.hotel.chain_name')} (EN)`] = item.chainName;
    dataPackage[`${t('management.hotel.chain_name')} (FR)`] = item.chainNameFr;
    dataPackage[t('management.package.dest_sv_id')] = item.destSVId;
    dataPackage[`${t('common.destination')} (EN)`] = item.destName;
    dataPackage[`${t('common.destination')} (FR)`] = item.destNameFr;
    dataPackage[t('management.hotel.max_rooms')] = item.maxRooms;
    dataPackage[t('management.hotel.quote_expiry_restriction')] = item.quoteExpiryRestriction;
    dataPackage[t('management.hotel.exclude_room')] = formatRoomExport(item.excludeRoomCategoryPackage);
    dataPackage[t('management.hotel.group_type')] = isExportVersion ? item.groupTypes?.join(', ') : convertListGroupType(item.groupTypes, groupTypeList.value);
    dataPackage[t('management.hotel.room_category_call_order')] = item.roomCategoryCallOrder;
    dataPackage[t('management.hotel.preferred')] = formatHotelPreferredExport(item.hotelPreferred);
    dataPackage[t('hotel_contract.black_out_date')] = handleDisplayDate(item.blackoutDate, ', ');
    dataPackage[t('hotel_contract.booking_date_range')] = handleDisplayDate(item.bookingDate, ', ');
    dataPackage[t('hotel_contract.travel_date_range')] = handleDisplayDate(item.travelDate, ', ');
    dataPackage[t('management.hotel.instaquote')] = item.insta;
    dataPackage[t('management.hotel.hotel_type')] = item.hotelType;
    dataPackage[t('common.status')] = item.status;
    return dataPackage;
  });
};

const formatDataExportHotel = (listData: any, isExportVersion?: boolean) => {
  return listData?.map((item: any) => {
    const dataHotel: any = {};
    dataHotel[t('management.hotel.tts_id')] = item.hCode;
    dataHotel[t('management.hotel.sv_id')] = item.code;
    dataHotel[`${t('management.hotel.hotel_name')} (EN)`] = item.name;
    dataHotel[`${t('management.hotel.hotel_name')} (FR)`] = item.nameFr;
    dataHotel[`${t('management.hotel.chain_name')} (EN)`] = item.chainName;
    dataHotel[`${t('management.hotel.chain_name')} (FR)`] = item.chainNameFr;
    dataHotel[t('management.package.dest_sv_id')] = item.destSVId;
    dataHotel[`${t('common.destination')} (EN)`] = item.destName;
    dataHotel[`${t('common.destination')} (FR)`] = item.destNameFr;
    dataHotel[t('management.hotel.max_rooms')] = item.maxRoomsHotel;
    dataHotel[t('management.hotel.quote_expiry_restriction')] = item.quoteExpiryRestriction;
    dataHotel[t('management.hotel.exclude_room')] = formatRoomExport(item.excludeRoomCategoryHotel);
    dataHotel[t('management.hotel.group_type')] = isExportVersion ? item.groupTypesHotel?.join(', ') : convertListGroupType(item.groupTypesHotel, groupTypeList.value);
    dataHotel[t('management.hotel.room_category_call_order')] = item.roomCategoryCallOrderHotel;
    dataHotel[t('management.hotel.preferred')] = formatHotelPreferredExport(item.hotelPreferredHotel);
    dataHotel[t('hotel_contract.black_out_date')] = handleDisplayDate(item.blackoutDateHotel, ', ');
    dataHotel[t('hotel_contract.booking_date_range')] = handleDisplayDate(item.bookingDateHotel, ', ');
    dataHotel[t('hotel_contract.travel_date_range')] = handleDisplayDate(item.travelDateHotel, ', ');
    dataHotel[t('management.hotel.instaquote')] = item.instaHotel;
    dataHotel[t('management.hotel.hotel_type')] = item.hotelTypeHotel;
    dataHotel[t('common.status')] = item.statusHotel;
    return dataHotel;
  });
};

const formatHotelPreferredExport = (data: any) => {
  const { sts, startDate, endDate } = data ?? {};

  const param = {
    start: startDate,
    end: endDate
  };

  if (sts === STATUS_OPEN) {
    return `${sts}, ${handleDisplayDate([param])}`;
  }
  return sts === STATUS_CLOSED ? sts : '';
};

const filterTab = (type: string, hotelValue: any) => {
  if (type === 'PACKAGE_TAB') {
    return hotelValue.filter((hotel: any) => hotel.isPackage);
  } else {
    return hotelValue.filter((hotel: any) => hotel.isHotel);
  }
};

const pageChange = () => {
  editingPaxRoomNumbers.value = [];
  paxRoomNumbers.value = {};
};

const getListExistGroupType = (data: any, groupTypes: any) => {
  return data?.filter((group: any) => groupTypes?.map((g: any) => g.sk).includes(group));
};

const getgroupTypeData = () => {
  isLoading.value = true;
  groupTypeManagement
    .getGroupTypeList()
    .then(async (response) => {
      groupTypeList.value = response.data;
      await hotelManagementStore.getListHotel().then((response) => {
        hotels.value = response?.map((hotel: any) => {
          hotel.groupTypes = getListExistGroupType(hotel.groupTypes, groupTypeList.value);
          hotel.groupTypesHotel = getListExistGroupType(hotel.groupTypesHotel, groupTypeList.value);
          return hotel;
        });

        sheetsDataPackage.value = [
          {
            name: 'Sheet 1',
            data: formatDataExportPackage(filterTab('PACKAGE_TAB', hotels.value))
          }
        ];

        sheetsDataHotel.value = [
          {
            name: 'Sheet 1',
            data: formatDataExportHotel(filterTab('HOTEL_TAB', hotels.value))
          }
        ];
      });
    })
    .finally(() => (isLoading.value = false));
};

const getAvgPaxRoom = () => {
  hotelManagementStore.getAvgPaxRoom().then((response) => {
    avgPaxRoomData.value = response.data?.avgPaxRoom;
  });
};

getgroupTypeData();
getAvgPaxRoom();

const clearFilterPackage = () => {
  initFiltersPackage();
};

const clearFilterHotel = () => {
  initFiltersHotel();
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};

const onUpdateStatus = async (code, status: string, type) => {
  try {
    await hotelManagementStore.updateHotelStatus(code, status, type);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};
const handleOpenChangeRole = (status: string) => {
  return (status === STATUS_OPEN && !authorizations?.configurationPage?.hotel_deactive) || (!authorizations?.configurationPage?.hotel_active && (status === STATUS_CLOSED || status === null));
};

const openNote = async (data: any, type: string) => {
  await notesManagementStore.handleGetListNotes(data.code, type);
  dialog.open(ListNote, {
    props: {
      header: t('my_quote.notes'),
      modal: true
    },
    data: {
      qId: data.code,
      type: type
    }
  });
};

const handleOnClose = async (value: any, data: any, type: string) => {
  if (value.data === true) {
    await hotelManagementStore.getListHotel();
  } else if (type === 'HOTEL') {
    data.hotelPreferredHotel.sts = STATUS_CLOSED;
  } else {
    data.hotelPreferred.sts = STATUS_CLOSED;
  }
};

const updateStatusPreferred = async (data: any, type: string, status: string) => {
  if (status === STATUS_OPEN) {
    const dialogRef = dialog.open(UpdatePreferredModal, {
      props: {
        header: t('management.hotel.update_preferred'),
        modal: true
      },
      data: {
        qId: data.code,
        sts: type === 'HOTEL' ? data.hotelPreferredHotel.sts : data.hotelPreferred.sts,
        type: type
      },
      onClose: async (value) => {
        await handleOnClose(value, data, type);
      },
      emits: {
        isSave: () => {
          dialogRef.close(true);
        },
        renderTable: () => {
          getgroupTypeData();
        }
      }
    });
  } else {
    try {
      await preferredManagementStore.handleUpdateStatusPreferred(data.code, type, data.hotelPreferred);
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    } catch (error) {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    }
  }
};

const handleDisplayDate = (listDate: any, separator = '\n') => {
  let format = '';
  if (listDate?.length > 0) {
    format = listDate.map((item: any) => `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`).join(separator);
  }
  return format;
};

const updateTypeCallRoom = async (code, event) => {
  hotelManagementStore
    .updateTypeCallRoom(code, event)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.update_success'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.update_error'), life: 5000 });
    });
};

const openModalAddHotel = () => {
  const dialogRef = dialog.open(AddHotelModal, {
    props: {
      header: t('management.hotel.add_manual_hotel'),
      modal: true,
      style: {
        width: '85vw'
      }
    },
    data: {
      isEdit: false,
      destinations: destinationList
    },
    emits: {
      isSave: () => {
        getgroupTypeData();
        dialogRef.close();
      }
    }
  });
};

const filterDataEdit = (type: string, hotelValue: any) => {
  let data: any = {
    hCode: hotelValue?.hCode,
    code: hotelValue?.code,
    name: hotelValue?.name,
    nameFr: hotelValue?.nameFr,
    chainName: hotelValue?.chainName,
    chainNameFr: hotelValue?.chainNameFr,
    quoteExpiryRestriction: hotelValue?.quoteExpiryRestriction,
    destName: hotelValue?.destName,
    destNameFr: hotelValue?.destNameFr,
    isPackage: hotelValue?.isPackage,
    isHotel: hotelValue?.isHotel,
    hotelType: hotelValue?.hotelType,
    hotelTypeHotel: hotelValue?.hotelTypeHotel,
    destSVId: hotelValue?.destSVId ?? '',
    excludeRoomCategoryHotel: hotelValue?.excludeRoomCategoryHotel,
    excludeRoomCategoryPackage: hotelValue?.excludeRoomCategoryPackage
  };
  if (type === 'PACKAGE_TAB') {
    data = {
      ...data,
      maxRooms: hotelValue?.maxRooms,
      insta: hotelValue?.insta,
      status: hotelValue?.status,
      instaHotel: hotelValue?.instaHotel,
      statusHotel: hotelValue?.statusHotel,
      excludeHotelRoom: hotelValue?.excludeHotelRoom,
      groupTypes: hotelValue?.groupTypes,
      roomCategoryCallOrder: hotelValue?.roomCategoryCallOrder,
      isPackageTab: true,
      travelDate: hotelValue?.travelDate ?? [],
      bookingDate: hotelValue?.bookingDate ?? [],
      blackoutDate: hotelValue?.blackoutDate ?? [],
      excludeRoomCategoryHotel: hotelValue?.excludeRoomCategoryHotel,
      excludeRoomCategoryPackage: hotelValue?.excludeRoomCategoryPackage
    };
  } else {
    data = {
      ...data,
      maxRooms: hotelValue?.maxRoomsHotel,
      insta: hotelValue?.insta,
      status: hotelValue?.status,
      instaHotel: hotelValue?.instaHotel,
      statusHotel: hotelValue?.statusHotel,
      excludeHotelRoom: hotelValue?.excludeHotelRoomHotel,
      groupTypes: hotelValue?.groupTypesHotel,
      roomCategoryCallOrder: hotelValue?.roomCategoryCallOrderHotel,
      isPackageTab: false,
      travelDate: hotelValue?.travelDateHotel ?? [],
      bookingDate: hotelValue?.bookingDateHotel ?? [],
      blackoutDate: hotelValue?.blackoutDateHotel ?? [],
      excludeRoomCategoryHotel: hotelValue?.excludeRoomCategoryHotel,
      excludeRoomCategoryPackage: hotelValue?.excludeRoomCategoryPackage
    };
  }
  return data;
};

const openModalEditHotel = (type: string, rowDataHotel: any) => {
  const data = filterDataEdit(type, rowDataHotel);

  const dialogRef = dialog.open(AddHotelModal, {
    props: {
      header: t('management.hotel.edit_hotel_details'),
      modal: true,
      style: {
        width: '85vw'
      }
    },
    data: {
      isEdit: true,
      data,
      rowDataHotel,
      destinations: destinationList
    },
    emits: {
      isSave: () => {
        getgroupTypeData();
        dialogRef.close();
      }
    }
  });
};

const handleDeleteHotel = async (id: string) => {
  try {
    await hotelManagementStore.deleteHotel(id);
    getgroupTypeData();
    toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
  } catch {
    toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
  }
};

const handleOpenModalDelete = (id: string) => {
  const dialogRef = dialog.open(ConfirmDeleteModal, {
    props: {
      header: t('management.hotel.confirm'),
      modal: true
    },
    data: {
      title: t('management.hotel.confirm_delete')
    },
    emits: {
      onSave: () => {
        handleDeleteHotel(id);
        dialogRef.close();
      }
    }
  });
};

const convertListGroupType = (data: any, groupTypes: any) => {
  const configList = groupTypes?.filter((item: any) => data?.includes(item.sk));

  const result = configList?.reduce((prev: any, cur: any) => {
    if (!prev) {
      return currentLanguage === LANG_ENGLISH ? cur.name : cur.nameFr;
    }
    return prev + ', ' + (currentLanguage === LANG_ENGLISH ? cur.name : cur.nameFr);
  }, '');

  return result;
};

const handleFormatListExcludeRoom = (dataList: any) => {
  if (dataList?.length) {
    return dataList.map((category: any) => {
      return {
        ...category,
        ranges: convertTimeZero(category.ranges, 'excludeCategory')
      };
    });
  } else {
    return [];
  }
};

const formatExcludeRoomCategory = (excludeRoom: any) => {
  return {
    categories: handleFormatListExcludeRoom(excludeRoom.categories),
    manualCategories: handleFormatListExcludeRoom(excludeRoom.manualCategories)
  };
};

const handleApplyTo = async (data: any, type: string) => {
  let payload;
  isLoading.value = true;
  if (type === 'PACKAGE_TAB') {
    payload = {
      ...data,
      maxRoomsHotel: data.maxRooms,
      excludeHotelRoomHotel: data.excludeHotelRoom,
      groupTypesHotel: data.groupTypes,
      hotelPreferredHotel: data.hotelPreferred,
      hotelTypeHotel: data.hotelType,
      instaHotel: data.insta,
      isHotel: true,
      roomCategoryCallOrderHotel: data.roomCategoryCallOrder,
      statusHotel: data.status,
      avgPaxRoomHotel: data.avgPaxRoom,
      blackoutDateHotel: convertTimeZero(data.blackoutDate),
      bookingDateHotel: convertTimeZero(data.bookingDate),
      noteHotel: data.note,
      travelDateHotel: convertTimeZero(data.travelDate),
      excludeRoomCategoryHotel: formatExcludeRoomCategory(data.excludeRoomCategoryPackage)
    };
  } else {
    payload = {
      ...data,
      maxRooms: data.maxRoomsHotel,
      excludeHotelRoom: data.excludeHotelRoomHotel,
      groupTypes: data.groupTypesHotel,
      hotelPreferred: data.hotelPreferredHotel,
      hotelType: data.hotelTypeHotel,
      insta: data.instaHotel,
      isPackage: true,
      roomCategoryCallOrder: data.roomCategoryCallOrderHotel,
      status: data.statusHotel,
      avgPaxRoom: data.avgPaxRoomHotel,
      blackoutDate: convertTimeZero(data.blackoutDateHotel),
      bookingDate: convertTimeZero(data.bookingDateHotel),
      note: data.noteHotel,
      travelDate: convertTimeZero(data.travelDateHotel),
      excludeRoomCategoryPackage: formatExcludeRoomCategory(data.excludeRoomCategoryHotel)
    };
  }

  await hotelManagementStore
    .addNewHotel(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      getgroupTypeData();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleImportFile = async (file: any, type: string) => {
  isRevert.value = false;
  isLoadingImport.value = true;
  isShowModal.value = true;
  isSuccess.value = true;
  try {
    const payload = new FormData();
    payload.append('file', file);
    payload.append('tabType', type);
    await hotelManagementStore.importFileHotel(payload);
    getgroupTypeData();
  } catch (error) {
    isSuccess.value = false;
  } finally {
    isLoadingImport.value = false;
  }
};

const handleApply = (data: any, file: any, type: string, typeModal: string) => {
  const dialogRef = dialog.open(ViewHotelDataModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      hotels: data,
      type: typeModal,
      sheetData: type === 'PACKAGE' ? formatDataExportPackage(data, true) : formatDataExportHotel(data, true),
      tabType: type
    },
    emits: {
      onApply: () => {
        dialogRef.close();
        if (typeModal === 'IMPORT') {
          handleImportFile(file, type);
        } else if (typeModal === 'REVERT') {
          handleRevertVersion(file, type);
        }
      },
      onClose: () => {
        dialogRef.close();
        if (typeModal === 'REVERT') {
          showRevertVersion(type);
        }
      }
    }
  });
};

const handleRevertVersion = (fileName: string, type: string) => {
  isRevert.value = true;
  isLoadingImport.value = true;
  isShowModal.value = true;
  const payload = {
    fileName,
    tabType: type
  };
  hotelManagementStore
    .revertVersion(payload)
    .then(() => {
      isSuccess.value = true;
      if (type === 'PACKAGE') {
        isPackageTab.value = true;
      } else {
        isPackageTab.value = false;
      }
      getgroupTypeData();
    })
    .catch(() => {
      isSuccess.value = false;
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingImport.value = false;
    });
};

const showModalImportExcel = (type: string) => {
  const dialogRef = dialog.open(ImportFileModal, {
    props: {
      header: t('common.labels.upload_hotel'),
      modal: true
    },
    data: {
      url: hotelManagementStore.getUrlUploadFile
    },
    emits: {
      onApply: (data: any, file: any) => {
        handleApply(data, file, type, 'IMPORT');
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleCloseModal = async (value: boolean) => {
  if (isRevert.value === true) {
    if (isPackageTab.value === true) {
      await showRevertVersion('PACKAGE');
    } else {
      await showRevertVersion('HOTEL');
    }
  }
  isShowModal.value = value;
};

const handleGetPreviewVersion = async (fileName: string, type: string, typeModal: string) => {
  isLoading.value = true;
  hotelManagementStore
    .getPreviewByFileName(fileName, type)
    .then((data) => {
      handleApply(data?.data, fileName, type, typeModal);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const showRevertVersion = async (type: string) => {
  hotelManagementStore
    .getVersionRevert(type)
    .then((versions) => {
      handleOpenModalRevert(versions?.data, type);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};

const handleOpenModalRevert = (versions: any, type: string) => {
  const dialogRef = dialog.open(ViewVersionRevertModal, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onPreview: (fileName: string) => {
        handleGetPreviewVersion(fileName, type, 'PREVIEW');
      },
      onRevert: async (fileName: string) => {
        await handleGetPreviewVersion(fileName, type, 'REVERT');
        dialogRef.close();
      },
      onExport: (fileName: string) => {
        handleGetPreviewVersion(fileName, type, 'EXPORT');
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const getListDestination = () => {
  packageManagementStore
    .getListDestination()
    .then((res) => {
      destinationList.value = res ?? [];
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};

getListDestination();

const getHotelConfiguration = (config: any) => {
  return config?.hotel_configuration ?? {};
};
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="card hotel-management">
    <div class="flex justify-content-between align-items-center">
      <h3 class="h5style">{{ $t('management.hotel.hotel_management') }}</h3>
      <div v-if="getHotelConfiguration(authorizations)?.add_hotel">
        <Button data-testid="btn_add_hotel" icon="pi pi-plus" :label="$t('management.hotel.add_hotel')" class="mb-2" @click="openModalAddHotel()" />
      </div>
    </div>
    <div>
      <Tabs :value="0">
        <TabList>
          <Tab :key="0" :value="0">{{ $t('management.hotel.package_configuration') }}</Tab>
          <Tab :key="1" :value="1">{{ $t('management.hotel.hotel_configuration') }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel :key="0" :value="0">
            <DataTable
              v-model:filters="filtersPackage"
              :value="filterTab('PACKAGE_TAB', hotels)"
              paginator
              showGridlines
              class="text-sm"
              :rows="10"
              dataKey="id"
              data-testid="table_hotel"
              filterDisplay="menu"
              :globalFilterFields="['hCode', 'code', 'name', 'chainName', 'destName', 'status', 'note', 'hotelPreferred.sts']"
              @page="pageChange"
            >
              <template #header>
                <div class="flex justify-content-between flex-col sm:flex-row">
                  <div class="flex justify-content-left gap-3">
                    <div>
                      <Button icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="clear-hotel-btn mb-2" @click="clearFilterPackage()" data-testid="button_clear" />
                    </div>
                    <div v-if="getHotelConfiguration(authorizations)?.export_hotel">
                      <ExportExcelFile class="export-hotel-btn" :sheets="sheetsDataPackage" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('PKG_Hotel_Management')"></ExportExcelFile>
                    </div>
                    <div v-if="getHotelConfiguration(authorizations)?.import_hotel">
                      <Button id="btn-import-package-tab" :label="$t('common.buttons.import')" class="import-hotel-btn" @click="showModalImportExcel('PACKAGE')" />
                    </div>
                    <div v-if="getHotelConfiguration(authorizations)?.revert_hotel">
                      <Button id="btn-revert-package-tab" type="button" :label="$t('common.buttons.revert')" class="revert-hotel-btn" @click="showRevertVersion('PACKAGE')" />
                    </div>
                  </div>
                  <div class="justify-content-right">
                    <span class="p-input-icon-left mb-2">
                      <i class="pi pi-search" />
                      <InputText data-testid="search_input" v-model="filtersPackage['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
                    </span>
                  </div>
                </div>
              </template>
              <template #empty> {{ $t('management.no_data_found') }} </template>
              <template #loading> {{ $t('common.generic.loading') }}. {{ $t('my_quote.please_wait') }}. </template>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="hCode" :header="$t('management.hotel.tts_id')" class="m-w-132">
                <template #body="{ data }">
                  {{ data.hCode }}
                </template>
                <template #filter="{ filterModel }">
                  <InputText data-testid="filter_hCode" v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_tts_id')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="code" class="m-w-128">
                <template #header>
                  <span class="text-center"> {{ $t('management.hotel.sv_id') }}</span>
                </template>
                <template #body="{ data }">
                  {{ data.code }}
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_soft_voyage_id')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="name" :header="$t('management.hotel.hotel_name')" class="min-w-240">
                <template #body="{ data }">
                  <div>{{ data.name }}</div>
                  <Divider type="solid" />
                  <div>{{ data.nameFr }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_hotel_name')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="chainName" :header="$t('management.hotel.chain_name')">
                <template #body="{ data }">
                  <div>{{ data.chainName }}</div>
                  <Divider type="solid" />
                  <div>{{ data.chainNameFr }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_chain_name')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="destSVId" :header="$t('management.package.dest_sv_id')">
                <template #body="{ data }">
                  <div class="text-sm">{{ data.destSVId }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="destName" :header="$t('common.destination')">
                <template #body="{ data }">
                  <div>{{ data.destName }}</div>
                  <Divider type="solid" />
                  <div>{{ data.destNameFr }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_destination')" />
                </template>
              </TableColumn>
              <TableColumn field="maxRooms" class="m-w-132">
                <template #header>
                  <div class="flex flex-col">
                    <div class="text-center">{{ $t('management.hotel.max_rooms') }}</div>
                    <div class="hidden">
                      <div class="font-normal text-sm">{{ $t('management.hotel.avg_pax_room') }}</div>
                      <div class="flex align-items-center gap-2">
                        <div>
                          <InputNumber v-if="!isEditAvgPaxRoom" class="input-header" v-model="avgPaxRoomData" :minFractionDigits="2" :maxFractionDigits="2" disabled />
                          <InputNumber v-else class="input-header" :min="0" :max="1000" v-model="avgPaxRoomTempoary" autofocus :minFractionDigits="2" :maxFractionDigits="2" />
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #body="{ data, index }">
                  <div class="flex align-items-center gap-2">
                    <div>
                      <InputNumber v-if="!editingPaxRoomNumbers.includes(index)" v-model="data.maxRooms" disabled class="input-body text-sm" />
                      <InputNumber v-else :min="0" :max="10000" v-model="paxRoomNumbers[index]" autofocus class="input-body text-sm" />
                    </div>
                  </div>
                </template>
              </TableColumn>
              <TableColumn field="quoteExpiryRestriction" :header="$t('management.hotel.quote_expiry_restriction')" class="m-w-132">
                <template #body="{ data }">
                  <div class="flex align-items-center gap-2">
                    <div>
                      <InputNumber v-model="data.quoteExpiryRestriction" disabled class="input-body text-sm" />
                    </div>
                  </div>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="excludeRoomCategoryPackage" :header="$t('management.hotel.exclude_room')">
                <template #body="{ data, field }">
                  <div class="flex align-items-center min-w-240">
                    <ViewRoomExclusions :excludeRooms="data[field]" :maxCharacters="165" />
                  </div>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="groupTypes" :header="$t('management.hotel.group_type')">
                <template #body="{ data }">
                  <div class="group-types-row">
                    {{ convertListGroupType(data.groupTypes, groupTypeList) }}
                  </div>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="groupTypeList" optionLabel="name" optionValue="sk" :placeholder="$t('common.select_one')" class="p-column-filter w-20rem" showClear>
                    <template #option="slotProps">
                      <div class="max-w-20rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr']">
                        {{ currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr'] }}
                      </div>
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" class="min-w-200" :show-add-button="false" field="roomCategoryCallOrder" :header="$t('management.hotel.room_category_call_order')">
                <template #body="{ data }">
                  <Select disabled class="table-select w-full" :options="apiOrder" optionLabel="label" optionValue="value" v-model="data.roomCategoryCallOrder" @change="updateTypeCallRoom(data.code, $event.value)">
                    <template #value="slotProps">
                      <div v-if="slotProps.value">
                        <div class="overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type) => type.value === slotProps.value)?.label }}</div>
                      </div>
                    </template>
                  </Select>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="apiOrder" optionLabel="label" optionValue="value" :placeholder="$t('common.select_one')" class="p-column-filter table-select w-full" showClear>
                    <template #value="slotProps">
                      <div v-if="slotProps.value">
                        <div class="w-6rem overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type) => type.value === slotProps.value)?.label }}</div>
                      </div>
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="hotelPreferred.sts" :header="$t('management.hotel.preferred')" class="m-w-128">
                <template #body="{ data }">
                  <ToggleSwitch
                    id="ip_switch_status_prefer"
                    :disabled="handleOpenChangeRole(data.hotelPreferred.sts)"
                    v-model="data.hotelPreferred.sts"
                    :trueValue="STATUS_OPEN"
                    :falseValue="STATUS_CLOSED"
                    @change="updateStatusPreferred(data, 'PACKAGE', data.hotelPreferred.sts)"
                  />
                  <div>
                    {{ formatDate(data.hotelPreferred.startDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                    {{ formatDate(data.hotelPreferred.endDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                  </div>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
                    <template #option="slotProps">
                      <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn class="min-w-200" field="bookingDate" :header="$t('hotel_contract.booking_date_range')" :showAddButton="false" :showFilterOperator="false" :showFilterMatchModes="false" dataType="date">
                <template #body="{ data, field }">
                  <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn class="min-w-200" field="travelDate" :header="$t('hotel_contract.travel_date_range')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false" dataType="date">
                <template #body="{ data, field }">
                  <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn class="min-w-200" field="blackoutDate" :header="$t('hotel_contract.black_out_date')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false" dataType="date">
                <template #body="{ data, field }">
                  <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="insta" :header="$t('management.hotel.instaquote')" class="m-w-128">
                <template #body="{ data, field }">
                  <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data.code, data[field], INSTA)" />
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
                    <template #option="slotProps">
                      <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="hotelType" :header="$t('management.hotel.hotel_type')">
                <template #body="{ data, field }">
                  <div>{{ data[field] }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="status" :header="$t('common.status')">
                <template #body="{ data, field }">
                  <div class="flex flex-column align-items-center">
                    <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data.code, data[field], STATUS)" />
                    <Button
                      v-if="getHotelConfiguration(authorizations)?.apply_to_hotel"
                      :label="$t('management.hotel.apply_to_hotel')"
                      class="p-button-outlined p-button-sm border-noround w-full mb-1 text-xs mt-1"
                      @click="handleApplyTo(data, 'PACKAGE_TAB')"
                    />
                  </div>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
                    <template #option="slotProps">
                      <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn field="notes" :header="$t('common.notes')">
                <template #body="{ data }">
                  <Button :label="$t('common.notes')" class="p-button-outlined border-noround w-full mb-1" @click="openNote(data, 'PACKAGE')" />
                </template>
              </TableColumn>
              <TableColumn :header="$t('common.action')">
                <template #body="{ data }">
                  <div class="flex">
                    <Button v-if="getHotelConfiguration(authorizations)?.edit_hotel" icon="pi pi-pencil" class="p-button-text" @click="openModalEditHotel('PACKAGE_TAB', data)" />
                    <Button v-if="getHotelConfiguration(authorizations)?.delete_hotel" icon="pi pi-trash" class="p-button-text" @click="handleOpenModalDelete(data?.code)" />
                  </div>
                </template>
              </TableColumn>
            </DataTable>
          </TabPanel>
          <TabPanel :key="1" :value="1">
            <DataTable
              v-model:filters="filtersHotel"
              :value="filterTab('HOTEL_TAB', hotels)"
              paginator
              showGridlines
              class="text-sm"
              :rows="10"
              dataKey="id"
              filterDisplay="menu"
              :globalFilterFields="['hCode', 'code', 'name', 'chainName', 'destName', 'statusHotel', 'noteHotel', 'hotelPreferredHotel.sts']"
              @page="pageChange"
            >
              <template #header>
                <div class="flex justify-content-between flex-col sm:flex-row">
                  <div class="flex justify-content-left gap-3">
                    <div>
                      <Button icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="clear-hotel-btn mb-2" @click="clearFilterHotel()" data-testid="button_clear_hotel" />
                    </div>
                    <div v-if="getHotelConfiguration(authorizations)?.export_hotel">
                      <ExportExcelFile class="export-hotel-btn" :sheets="sheetsDataHotel" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('HTL_Hotel_Management')"></ExportExcelFile>
                    </div>
                    <div v-if="getHotelConfiguration(authorizations)?.import_hotel">
                      <Button id="btn-import-hotel-tab" :label="$t('common.buttons.import')" class="import-hotel-btn" @click="showModalImportExcel('HOTEL')" />
                    </div>
                    <div v-if="getHotelConfiguration(authorizations)?.revert_hotel">
                      <Button id="btn-revert-hotel-tab" type="button" :label="$t('common.buttons.revert')" class="revert-hotel-btn" @click="showRevertVersion('HOTEL')" />
                    </div>
                  </div>

                  <div class="justify-content-right">
                    <span class="p-input-icon-left mb-2">
                      <i class="pi pi-search" />
                      <InputText data-testid="search_input_hotel" v-model="filtersHotel['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
                    </span>
                  </div>
                </div>
              </template>
              <template #empty> {{ $t('management.no_data_found') }} </template>
              <template #loading> {{ $t('common.generic.loading') }}. {{ $t('my_quote.please_wait') }}. </template>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="hCode" :header="$t('management.hotel.tts_id')" class="m-w-132">
                <template #body="{ data }">
                  {{ data.hCode }}
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_tts_id')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="code" class="m-w-128">
                <template #header>
                  <span class="text-center"> {{ $t('management.hotel.sv_id') }}</span>
                </template>
                <template #body="{ data }">
                  {{ data.code }}
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_soft_voyage_id')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="name" :header="$t('management.hotel.hotel_name')" class="min-w-240">
                <template #body="{ data }">
                  <div>{{ data.name }}</div>
                  <Divider type="solid" />
                  <div>{{ data.nameFr }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_hotel_name')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="chainName" :header="$t('management.hotel.chain_name')">
                <template #body="{ data }">
                  <div>{{ data.chainName }}</div>
                  <Divider type="solid" />
                  <div>{{ data.chainNameFr }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_chain_name')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="destSVId" :header="$t('management.package.dest_sv_id')">
                <template #body="{ data }">
                  <div class="text-sm">{{ data.destSVId }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="destName" :header="$t('common.destination')">
                <template #body="{ data }">
                  <div>{{ data.destName }}</div>
                  <Divider type="solid" />
                  <div>{{ data.destNameFr }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_destination')" />
                </template>
              </TableColumn>
              <TableColumn field="maxRooms" class="m-w-132">
                <template #header>
                  <div class="flex flex-col">
                    <div class="text-center">{{ $t('management.hotel.max_rooms') }}</div>
                    <div class="hidden">
                      <div class="font-normal text-sm">{{ $t('management.hotel.avg_pax_room') }}</div>
                      <div class="flex align-items-center gap-2">
                        <div>
                          <InputNumber v-if="!isEditAvgPaxRoom" class="input-header" v-model="avgPaxRoomData" :minFractionDigits="2" :maxFractionDigits="2" disabled />
                          <InputNumber v-else class="input-header" :min="0" :max="1000" v-model="avgPaxRoomTempoary" autofocus :minFractionDigits="2" :maxFractionDigits="2" />
                        </div>
                        <div>
                          <div v-if="!isEditAvgPaxRoom">
                            <i class="pi pi-pencil" @click="editAvgPaxRoom()"></i>
                          </div>
                          <div v-else class="flex gap-1">
                            <i class="pi pi-check edit-pi" @click="saveAvgPaxRoom()"></i>
                            <i class="pi pi-times edit-pi" @click="cancelEditAvgPaxRoom()"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #body="{ data, index }">
                  <div class="flex align-items-center gap-2">
                    <div>
                      <InputNumber v-if="!editingPaxRoomNumbers.includes(index)" v-model="data.maxRoomsHotel" disabled class="input-body text-sm" />
                      <InputNumber v-else :min="0" :max="10000" v-model="paxRoomNumbers[index]" autofocus class="input-body text-sm" />
                    </div>
                  </div>
                </template>
              </TableColumn>
              <TableColumn field="quoteExpiryRestriction" :header="$t('management.hotel.quote_expiry_restriction')" class="m-w-132">
                <template #body="{ data }">
                  <div class="flex align-items-center gap-2">
                    <div>
                      <InputNumber v-model="data.quoteExpiryRestriction" disabled class="input-body text-sm" />
                    </div>
                  </div>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="excludeRoomCategoryHotel" :header="$t('management.hotel.exclude_room')">
                <template #body="{ data, field }">
                  <div class="flex align-items-center min-w-240">
                    <ViewRoomExclusions :excludeRooms="data[field]" :maxCharacters="165" />
                  </div>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="groupTypesHotel" :header="$t('management.hotel.group_type')">
                <template #body="{ data }">
                  <div class="group-types-row">
                    {{ convertListGroupType(data.groupTypesHotel, groupTypeList) }}
                  </div>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="groupTypeList" optionLabel="name" optionValue="sk" :placeholder="$t('common.select_one')" class="p-column-filter w-20rem" showClear>
                    <template #option="slotProps">
                      <div class="max-w-20rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr']">
                        {{ currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr'] }}
                      </div>
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" class="min-w-200" :show-add-button="false" field="roomCategoryCallOrderHotel" :header="$t('management.hotel.room_category_call_order')">
                <template #body="{ data }">
                  <Select disabled class="table-select w-full" :options="apiOrder" optionLabel="label" optionValue="value" v-model="data.roomCategoryCallOrderHotel" @change="updateTypeCallRoom(data.code, $event.value)">
                    <template #value="slotProps">
                      <div v-if="slotProps.value">
                        <div class="overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type) => type.value === slotProps.value)?.label }}</div>
                      </div>
                    </template>
                  </Select>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="apiOrder" optionLabel="label" optionValue="value" :placeholder="$t('common.select_one')" class="p-column-filter table-select w-full" showClear>
                    <template #value="slotProps">
                      <div v-if="slotProps.value">
                        <div class="w-6rem overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type) => type.value === slotProps.value)?.label }}</div>
                      </div>
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="hotelPreferredHotel.sts" :header="$t('management.hotel.preferred')" class="m-w-128">
                <template #body="{ data }">
                  <ToggleSwitch
                    :disabled="handleOpenChangeRole(data.hotelPreferredHotel.sts)"
                    v-model="data.hotelPreferredHotel.sts"
                    :trueValue="STATUS_OPEN"
                    :falseValue="STATUS_CLOSED"
                    @change="updateStatusPreferred(data, 'HOTEL', data.hotelPreferredHotel.sts)"
                  />
                  <div>
                    {{ formatDate(data.hotelPreferredHotel.startDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                    {{ formatDate(data.hotelPreferredHotel.endDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                  </div>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
                    <template #option="slotProps">
                      <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn class="min-w-200" field="bookingDateHotel" :header="$t('hotel_contract.booking_date_range')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false" dataType="date">
                <template #body="{ data, field }">
                  <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn class="min-w-200" field="travelDateHotel" :header="$t('hotel_contract.travel_date_range')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false" dataType="date">
                <template #body="{ data, field }">
                  <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn class="min-w-200" field="blackoutDateHotel" :header="$t('hotel_contract.black_out_date')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false" dataType="date">
                <template #body="{ data, field }">
                  <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="instaHotel" :header="$t('management.hotel.instaquote')" class="m-w-128">
                <template #body="{ data, field }">
                  <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data.code, data[field], INSTA)" />
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
                    <template #option="slotProps">
                      <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="hotelTypeHotel" :header="$t('management.hotel.hotel_type')">
                <template #body="{ data, field }">
                  <div>{{ data[field] }}</div>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="statusHotel" :header="$t('common.status')">
                <template #body="{ data, field }">
                  <div class="flex flex-column align-items-center">
                    <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data.code, data[field], STATUS)" />
                    <Button
                      v-if="getHotelConfiguration(authorizations)?.apply_to_package"
                      :label="$t('management.hotel.apply_to_package')"
                      class="p-button-outlined p-button-sm border-noround w-full mb-1 text-xs mt-1"
                      @click="handleApplyTo(data, 'HOTEL_TAB')"
                    />
                  </div>
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
                    <template #option="slotProps">
                      <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn field="noteHotel" :header="$t('common.notes')" id="table_column">
                <template #body="{ data }">
                  <Button :label="$t('common.notes')" data-testid="btn_note_hotel" class="p-button-outlined border-noround w-full mb-1" @click="openNote(data, 'HOTEL')" />
                </template>
              </TableColumn>
              <TableColumn :header="$t('common.action')">
                <template #body="{ data }">
                  <div class="flex">
                    <Button v-if="getHotelConfiguration(authorizations)?.edit_hotel" icon="pi pi-pencil" class="p-button-text" @click="openModalEditHotel('HOTEL_TAB', data)" />
                    <Button v-if="getHotelConfiguration(authorizations)?.delete_hotel" icon="pi pi-trash" class="p-button-text" @click="handleOpenModalDelete(data?.code)" />
                  </div>
                </template>
              </TableColumn>
            </DataTable>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <Dialog v-model:visible="isShowModal" :header="$t('common.labels.uploading_hotel')" :closable="!isLoadingImport" modal>
        <ProgressModal :isProgress="isLoadingImport" @handleClose="handleCloseModal" :isSuccess="isSuccess" :isRevert="isRevert" />
      </Dialog>
    </div>
  </div>
</template>
